/**
 * Table Component
 *
 * A fully customizable, production-ready table component with glassmorphism styling,
 * infinite scrolling, sorting, expandable rows, and skeleton loading states.
 *
 * @component
 * @features
 * - **Custom Columns**: Configure columns with custom renderers, widths, and styling
 * - **Expandable Rows**: Built-in row expansion with custom content support
 * - **Infinite Scroll**: Automatic data loading when scrolling near bottom
 * - **Sorting**: Click column headers to sort data (ascending/descending)
 * - **Skeleton Loading**: Dynamic skeleton rows matching column configuration
 * - **Responsive Design**: Mobile-friendly with horizontal scroll on small screens
 * - **Glassmorphism Theme**: Integrated with Gigacore's design system
 * - **Performance**: Memoized callbacks and optimized re-rendering
 *
 * @example Basic Usage
 * const columns = [
 *   {
 *     key: 'name',
 *     label: 'Name',
 *     width: '200px',
 *     sortable: true
 *   },
 *   {
 *     key: 'email',
 *     label: 'Email',
 *     renderCell: (value) => <a href={`mailto:${value}`}>{value}</a>
 *   }
 * ];
 *
 * <Table
 *   columns={columns}
 *   data={users}
 *   loading={loading}
 *   hoverable
 *   striped
 * />
 *
 * @example With Expandable Rows
 * <Table
 *   columns={columns}
 *   data={data}
 *   expandable
 *   renderExpandedRow={(row) => (
 *     <div>
 *       <h4>Details for {row.name}</h4>
 *       <p>{row.description}</p>
 *     </div>
 *   )}
 * />
 *
 * @example With Infinite Scroll
 * const [data, setData] = useState([]);
 * const [page, setPage] = useState(1);
 * const [hasMore, setHasMore] = useState(true);
 * const [loadingMore, setLoadingMore] = useState(false);
 *
 * const fetchMoreData = async () => {
 *   setLoadingMore(true);
 *   const newData = await fetch(`/api/data?page=${page + 1}`);
 *   if (newData.length === 0) {
 *     setHasMore(false);
 *   } else {
 *     setData([...data, ...newData]);
 *     setPage(page + 1);
 *   }
 *   setLoadingMore(false);
 * };
 *
 * <Table
 *   columns={columns}
 *   data={data}
 *   infiniteScroll
 *   onLoadMore={fetchMoreData}
 *   hasMore={hasMore}
 *   loadingMore={loadingMore}
 *   maxHeight="600px"
 *   scrollThreshold={100}
 * />
 *
 * @param {Object} props - Component props
 * @param {Array<Object>} props.columns - Column configuration array
 * @param {string} props.columns[].key - Data key to map to this column (required)
 * @param {string} [props.columns[].label] - Column header label
 * @param {string} [props.columns[].width] - Column width (e.g., '200px', '20%')
 * @param {Function} [props.columns[].renderHeader] - Custom header renderer: (column) => ReactNode
 * @param {Function} [props.columns[].renderCell] - Custom cell renderer: (value, row, index) => ReactNode
 * @param {string} [props.columns[].headerClassName] - CSS class for header cell
 * @param {string} [props.columns[].cellClassName] - CSS class for data cells
 * @param {string} [props.columns[].align='left'] - Column content alignment: 'left' | 'center' | 'right'
 * @param {boolean} [props.columns[].sortable=true] - Enable sorting for this column (if table sortable=true)
 * @param {Array<Object>} props.data - Array of data objects to display
 * @param {boolean} [props.expandable=false] - Enable row expansion with +/- buttons
 * @param {Function} [props.renderExpandedRow] - Render function for expanded content: (row, index) => ReactNode
 * @param {Function} [props.onRowClick] - Row click handler: (row, index) => void
 * @param {string} [props.className=''] - Additional CSS classes for container
 * @param {string} [props.emptyMessage='No data available'] - Message shown when data is empty
 * @param {boolean} [props.loading=false] - Show skeleton loading state
 * @param {boolean} [props.stickyHeader=false] - Keep header visible when scrolling
 * @param {boolean} [props.striped=false] - Alternate row background colors
 * @param {boolean} [props.hoverable=true] - Enable row hover effects
 * @param {boolean} [props.infiniteScroll=false] - Enable infinite scrolling
 * @param {Function} [props.onLoadMore] - Callback to load more data: () => void
 * @param {boolean} [props.hasMore=false] - Whether more data is available to load
 * @param {boolean} [props.loadingMore=false] - Loading more data state
 * @param {number} [props.scrollThreshold=100] - Distance from bottom (px) to trigger load
 * @param {string} [props.maxHeight] - Max height for scrollable area (e.g., '600px')
 * @param {boolean} [props.sortable=false] - Enable column sorting
 * @param {number} [props.skeletonRows=5] - Number of skeleton rows to show when loading
 *
 * @returns {React.ReactElement} Rendered table component
 *
 * @see {@link /client/src/components/Table/README.md} for complete documentation
 * @see {@link /client/src/components/Table/USAGE_GUIDE.md} for usage examples
 */

import React, { useCallback, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import useTable from './hooks/useTable';
import './Table.scss';

export const Table = ({
  columns,
  data,
  expandable = false,
  renderExpandedRow = null,
  onRowClick = null,
  className = '',
  emptyMessage = 'No data available',
  loading = false,
  stickyHeader = false,
  striped = false,
  hoverable = true,
  infiniteScroll = false,
  onLoadMore = null,
  hasMore = false,
  loadingMore = false,
  scrollThreshold = 100,
  maxHeight = null,
  sortable = false,
  skeletonRows = 5,
}) => {
  const {
    expandedRows,
    toggleRowExpansion,
    isRowExpanded,
    sortConfig,
    handleSort,
    sortData,
    createScrollHandler,
  } = useTable();

  /**
   * Handles row click events
   * @param {Object} row - The row data object
   * @param {number} index - The row index
   */
  const handleRowClick = useCallback(
    (row, index) => {
      if (onRowClick) {
        onRowClick(row, index);
      }
    },
    [onRowClick]
  );

  /**
   * Handles expand/collapse button clicks
   * Stops event propagation to prevent row click from firing
   * @param {Event} e - Click event
   * @param {number} index - Row index to toggle
   */
  const handleExpandToggle = useCallback(
    (e, index) => {
      e.stopPropagation();
      toggleRowExpansion(index);
    },
    [toggleRowExpansion]
  );

  /**
   * Check if data array is empty or undefined
   * @type {boolean}
   */
  const isEmpty = !data || data.length === 0;

  /**
   * Sorted data based on current sort configuration
   * Only applies sorting if sortable prop is enabled
   * @type {Array<Object>}
   */
  const sortedData = useMemo(
    () => (sortable ? sortData(data) : data),
    [sortable, sortData, data]
  );

  /**
   * Calculate colspan for full-width rows (empty state, loading more)
   * Includes extra column for expand button if expandable is enabled
   * @type {number}
   */
  const fullColSpan = useMemo(
    () => columns.length + (expandable ? 1 : 0),
    [columns.length, expandable]
  );

  /**
   * Build table CSS class names based on props
   * @type {string}
   */
  const tableClassNames = useMemo(
    () =>
      [
        'custom-table',
        stickyHeader && 'sticky-header',
        striped && 'striped',
        hoverable && 'hoverable',
      ]
        .filter(Boolean)
        .join(' '),
    [stickyHeader, striped, hoverable]
  );

  /**
   * Create scroll handler for infinite scroll functionality
   * Only created when infiniteScroll prop is enabled
   * @type {Function|null}
   */
  const handleScroll = useMemo(() => {
    if (infiniteScroll) {
      return createScrollHandler(
        onLoadMore,
        hasMore,
        loading || loadingMore,
        scrollThreshold
      );
    }
    return null;
  }, [
    infiniteScroll,
    createScrollHandler,
    onLoadMore,
    hasMore,
    loading,
    loadingMore,
    scrollThreshold,
  ]);

  /**
   * Handle scroll events on table wrapper
   * Delegates to infinite scroll handler if enabled
   * @param {Event} e - Scroll event
   */
  const handleScrollEvent = useCallback(
    (e) => {
      if (handleScroll) {
        handleScroll(e);
      }
    },
    [handleScroll]
  );

  /**
   * Check if a specific column is sortable
   * Column is sortable if table sortable=true AND column.sortable !== false
   * @param {Object} column - Column configuration object
   * @returns {boolean} True if column is sortable
   */
  const isColumnSortable = useCallback(
    (column) => sortable && column.sortable !== false,
    [sortable]
  );

  /**
   * Generate skeleton loading rows that match column configuration
   * Creates placeholder rows with shimmer animation during loading state
   * @returns {Array<React.ReactElement>} Array of skeleton row elements
   */
  const renderSkeletonRows = useCallback(() => {
    return Array.from({ length: skeletonRows }).map((_, rowIndex) => (
      <tr key={`skeleton-${rowIndex}`} className='table-skeleton-row'>
        {expandable && (
          <td className='expand-column'>
            <div className='skeleton skeleton-circle' />
          </td>
        )}
        {columns.map((column) => (
          <td
            key={column.key}
            style={{ 
              width: column.width || 'auto',
              textAlign: column.align || 'left'
            }}
            className={column.cellClassName || ''}
          >
            <div className='skeleton skeleton-text' />
          </td>
        ))}
      </tr>
    ));
  }, [skeletonRows, expandable, columns]);

  /**
   * Build CSS class names for table header cells
   * Adds 'sortable' and 'sorted' classes based on column configuration
   * @param {Object} column - Column configuration object
   * @returns {string} Space-separated CSS class names
   */
  const getHeaderClassName = useCallback(
    (column) => {
      return [
        column.headerClassName || '',
        isColumnSortable(column) && 'sortable',
        sortConfig.key === column.key && 'sorted',
      ]
        .filter(Boolean)
        .join(' ');
    },
    [isColumnSortable, sortConfig.key]
  );

  return (
    <div className={`table-container ${className}`}>
      <div
        className='table-wrapper'
        onScroll={handleScrollEvent}
        style={maxHeight ? { maxHeight, overflowY: 'auto' } : {}}
      >
        <table className={tableClassNames}>
          <thead>
            <tr>
              {expandable && <th className='expand-column'></th>}
              {columns.map((column) => (
                <th
                  key={column.key}
                  style={{ 
                    width: column.width || 'auto',
                    textAlign: column.align || 'left'
                  }}
                  className={getHeaderClassName(column)}
                  onClick={() =>
                    isColumnSortable(column) && handleSort(column.key)
                  }
                >
                  <div 
                    className='header-content'
                    style={{
                      justifyContent: 
                        column.align === 'center' ? 'center' :
                        column.align === 'right' ? 'flex-end' :
                        'space-between'
                    }}
                  >
                    {column.renderHeader
                      ? column.renderHeader(column)
                      : column.label || column.key}
                    {isColumnSortable(column) && (
                      <span className='sort-icon'>
                        {sortConfig.key === column.key
                          ? sortConfig.direction === 'asc'
                            ? '↑'
                            : '↓'
                          : '↕'}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              renderSkeletonRows()
            ) : isEmpty ? (
              <tr className='table-empty-row'>
                <td colSpan={fullColSpan}>
                  <div className='table-empty'>
                    <p>{emptyMessage}</p>
                  </div>
                </td>
              </tr>
            ) : (
              sortedData.map((row, rowIndex) => {
                const rowClassName = [
                  'table-row',
                  onRowClick && 'clickable',
                  isRowExpanded(rowIndex) && 'expanded',
                ]
                  .filter(Boolean)
                  .join(' ');

                return (
                  <React.Fragment key={rowIndex}>
                    <tr
                      className={rowClassName}
                      onClick={() => handleRowClick(row, rowIndex)}
                    >
                      {expandable && (
                        <td className='expand-column'>
                          <button
                            className='expand-button'
                            onClick={(e) => handleExpandToggle(e, rowIndex)}
                            aria-label={
                              isRowExpanded(rowIndex)
                                ? 'Collapse row'
                                : 'Expand row'
                            }
                          >
                            {isRowExpanded(rowIndex) ? '−' : '+'}
                          </button>
                        </td>
                      )}
                      {columns.map((column) => (
                        <td
                          key={column.key}
                          style={{ 
                            width: column.width || 'auto',
                            textAlign: column.align || 'left'
                          }}
                          className={column.cellClassName || ''}
                        >
                          {column.renderCell
                            ? column.renderCell(row[column.key], row, rowIndex)
                            : row[column.key]}
                        </td>
                      ))}
                    </tr>
                    {expandable &&
                      isRowExpanded(rowIndex) &&
                      renderExpandedRow && (
                        <tr className='expanded-row'>
                          <td colSpan={fullColSpan}>
                            <div className='expanded-content'>
                              {renderExpandedRow(row, rowIndex)}
                            </div>
                          </td>
                        </tr>
                      )}
                  </React.Fragment>
                );
              })
            )}
            {/* Loading more indicator for infinite scroll */}
            {infiniteScroll && loadingMore && !loading && (
              <tr className='table-loading-more-row'>
                <td colSpan={fullColSpan}>
                  <div className='table-loading-more'>
                    <div className='spinner' />
                    <p>Loading more...</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

Table.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string,
      width: PropTypes.string,
      renderHeader: PropTypes.func,
      renderCell: PropTypes.func,
      headerClassName: PropTypes.string,
      cellClassName: PropTypes.string,
      align: PropTypes.oneOf(['left', 'center', 'right']),
      sortable: PropTypes.bool,
    })
  ).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  expandable: PropTypes.bool,
  renderExpandedRow: PropTypes.func,
  onRowClick: PropTypes.func,
  className: PropTypes.string,
  emptyMessage: PropTypes.string,
  loading: PropTypes.bool,
  stickyHeader: PropTypes.bool,
  striped: PropTypes.bool,
  hoverable: PropTypes.bool,
  infiniteScroll: PropTypes.bool,
  onLoadMore: PropTypes.func,
  hasMore: PropTypes.bool,
  loadingMore: PropTypes.bool,
  scrollThreshold: PropTypes.number,
  maxHeight: PropTypes.string,
  sortable: PropTypes.bool,
  skeletonRows: PropTypes.number,
};
